import { useAuth } from "@/src/contexts/auth.context";
import {
  CacheStorage,
  defaultCacheStorage,
} from "@/src/services/cache/cache-storage";
import {
  CacheIdentity,
  restoreCache,
  subscribeCache,
} from "@/src/services/cache/query-persistence";
import { clearScrollOffsets } from "@/src/hooks/common/use-scroll-restoration";
import { clearStableFeeds } from "@/src/hooks/features/posts/use-posts";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";

type Props = Readonly<{ children: React.ReactNode; storage?: CacheStorage }>;

/**
 * Restores and keeps the persisted query cache for the signed-in user in their
 * active organization. When that identity changes (sign-out, account or org
 * switch) every persisted cache is wiped first; the auth context clears the
 * in-memory cache on those same paths.
 *
 * Children are not held back: a screen that mounts before the stored cache is
 * read shows its skeleton for those few milliseconds and then the restored
 * data, while its own fetch carries on in the background.
 */
export default function QueryCachePersistence({ children, storage }: Props) {
  const { user, orgId, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const store = useRef(storage ?? defaultCacheStorage()).current;

  const userId = user?.itemId;
  const identityKey =
    isAuthenticated && userId && orgId ? `${userId}:${orgId}` : null;

  const previous = useRef<string | null>(null);
  const stopping = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    let cancelled = false;
    let stop: (() => Promise<void>) | null = null;
    const before = previous.current;
    previous.current = identityKey;

    void (async () => {
      await stopping.current;
      if (before && before !== identityKey) {
        clearStableFeeds();
        clearScrollOffsets();
        await store.removeAll().catch(() => {});
      }
      if (cancelled || !identityKey) return;
      const identity: CacheIdentity = {
        userId: identityKey.split(":")[0],
        orgId: identityKey.slice(identityKey.indexOf(":") + 1),
      };
      await restoreCache(store, queryClient, identity).catch(() => 0);
      if (cancelled) return;
      stop = subscribeCache(store, queryClient, identity);
    })();

    return () => {
      cancelled = true;
      const pending = stopping.current;
      stopping.current = pending.then(() => stop?.());
    };
  }, [identityKey, queryClient, store]);

  return <>{children}</>;
}
